import { useModeAwareQuery } from './useModeAwareQuery';
import { useApi } from './useApi';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import type { CollectionCaseListItemApi } from './useCollectionCasesData';
import type { CollectionCase } from '@/types/collections';

interface PaginationInfo {
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface CollectionCasesApiResponse {
  data: CollectionCaseListItemApi[];
  pagination: PaginationInfo;
}

export interface CustomerCollectionCasesParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  allCompanies?: boolean;
}

/** Map a frontend column ID to the backend SortBy parameter value */
function mapSortColumn(col?: string): string | undefined {
  if (!col) return undefined;
  const map: Record<string, string> = {
    priority: 'Priority',
    caseId: 'CaseNumber',
    customerName: 'CustomerName',
    amount: 'Amount',
    outstanding: 'Amount',
    gross: 'Amount',
    daysInCollection: 'DaysInCollection',
    nextAction: 'NextAction',
    nextActionDate: 'NextActionDate',
    status: 'Status',
    strategy: 'StrategyName',
    riskLevel: 'RiskLevel',
    assignedTo: 'AssignedToName',
    createdAt: 'CreatedAt',
    updatedAt: 'UpdatedAt',
    lastAction: 'LastActionDate',
    customerAccountNumber: 'CustomerAccountNumber',
  };
  return map[col] ?? col;
}

/** Map a single API list item to the frontend CollectionCase shape */
function mapApiItemToCollectionCase(item: CollectionCaseListItemApi): CollectionCase {
  return {
    id: item.caseId,
    caseId: item.caseId,
    caseNumber: item.caseNumber || undefined,
    customerId: String(item.customerId),
    customerAccountNumber: item.customerAccountNumber || undefined,
    customerName: item.customerName,
    customerEmail: item.customerEmail ?? '',
    customerPhone: item.customerPhone ?? '',
    priority: item.priority as CollectionCase['priority'],
    status: item.status as CollectionCase['status'],
    strategy: item.strategyName
      ? { id: String(item.strategyId ?? ''), name: item.strategyName, type: 'Standard' as const }
      : { id: '', name: '', type: 'Standard' as const },
    strategyPhase: item.strategyId
      ? {
          currentPhase: item.currentStage,
          totalPhases: item.totalStages,
          phaseName: `Stage ${item.currentStage}`,
        }
      : {
          currentPhase: 1,
          totalPhases: 1,
          phaseName: "Manual",
        },
    amount: item.amount,
    baseAmount: item.baseAmount ?? 0,
    displayOutstandingAmount: item.displayOutstandingAmount ?? 0,
    grossAmount: item.grossAmount ?? 0,
    displayGrossAmount: item.displayGrossAmount ?? 0,
    daysInCollection: item.daysInCollection,
    nextAction: item.nextAction ?? '',
    nextActionDate: item.nextActionDate ? new Date(item.nextActionDate) : new Date(),
    riskLevel: item.riskLevel as CollectionCase['riskLevel'],
    createdAt: new Date(item.createdAt),
    updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(item.createdAt),
    isPaused: item.isPaused,
    assignedTo: item.assignedToName ?? undefined,
    assignedUserId: item.assignedTo ?? undefined,
    contactId: item.contactId ? String(item.contactId) : undefined,
    notes: item.notes ?? undefined,
    currency: item.currencyCode ?? undefined,
    companyName: item.companyName ?? undefined,
    lastAction: item.lastActionType
      ? {
          type: item.lastActionType,
          description: item.lastActionDescription ?? '',
          date: item.lastActionDate ? new Date(item.lastActionDate) : new Date(),
          performedBy: item.lastActionActorName ?? '',
        }
      : undefined,
  };
}

/**
 * Hook to fetch collection cases for a specific customer with server-side
 * pagination, sorting and filtering.
 *
 * In **live** mode it calls GET /api/v1/collection-cases?CustomerId=X with
 * the supplied pagination / sort / search parameters.
 *
 * In **demo** mode it returns mock data from the global data store.
 */
export function useCustomerCollectionCases(
  customerId: string | undefined,
  params: CustomerCollectionCasesParams = { page: 1, pageSize: 25 },
) {
  const { apiGet } = useApi();

  // ── Demo data ────────────────────────────────────────────────────────
  const store = getGlobalDataStore();
  const allCases = store.getAllCases();
  const demoCases = customerId
    ? allCases.filter((c) => c.customerId === customerId)
    : [];

  const demoResult: { cases: CollectionCase[]; pagination: PaginationInfo } = {
    cases: demoCases,
    pagination: {
      totalItems: demoCases.length,
      page: 1,
      pageSize: params.pageSize,
      totalPages: Math.ceil(demoCases.length / params.pageSize) || 1,
    },
  };

  // ── Live fetcher ─────────────────────────────────────────────────────
  const result = useModeAwareQuery<
    { cases: CollectionCase[]; pagination: PaginationInfo },
    Error
  >(
    [
      'customer-collection-cases',
      customerId,
      params.page,
      params.pageSize,
      params.sortBy,
      params.sortOrder,
      params.search,
      params.allCompanies,
    ],
    async ({ signal }) => {
      if (!customerId) {
        return {
          cases: [],
          pagination: { totalItems: 0, page: 1, pageSize: params.pageSize, totalPages: 0 },
        };
      }

      const apiParams: Record<string, string | number | boolean> = {
        CustomerId: Number(customerId),
        Page: params.page,
        PageSize: params.pageSize,
      };

      if (params.sortBy) {
        const backendSort = mapSortColumn(params.sortBy);
        if (backendSort) {
          apiParams.SortBy = backendSort;
          apiParams.SortOrder = params.sortOrder ?? 'asc';
        }
      }

      if (params.search) {
        apiParams.Search = params.search;
      }

      if (params.allCompanies) {
        apiParams.AllCompanies = true;
      }

      const response = await apiGet<CollectionCasesApiResponse>(
        '/api/v1/collection-cases',
        apiParams,
        signal,
      );

      return {
        cases: response.data.map(mapApiItemToCollectionCase),
        pagination: response.pagination,
      };
    },
    demoResult,
    {
      enabled: !!customerId,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  return {
    cases: result.data?.cases ?? [],
    pagination: result.data?.pagination ?? {
      totalItems: 0,
      page: 1,
      pageSize: params.pageSize,
      totalPages: 0,
    },
    isLoading: result.isLoading,
    error: result.error,
  };
}
